// src/components/CookieBanner.jsx
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { FiX, FiChevronLeft, FiChevronDown, FiCheck } from "react-icons/fi";
import Logo from '../assets/img/LogoYouka.png';



const STORAGE_KEY = "youka_consents_v1";

/** القيم الافتراضية */
const defaultConsent = {
  version: 1,
  timestamp: null,
  categories: {
    essential: true,      // إجباري ومقفل
    statistics: false,
    marketing: false,
    external: false,
  },
  history: [], // [{timestamp, categories}]
};

/** أدوات التخزين */
const loadConsent = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.categories?.essential) parsed.categories.essential = true;
    return parsed;
  } catch {
    return null;
  }
};

const saveConsent = (consent) => {
  const obj = { ...consent, timestamp: Date.now() };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
  try {
    window.dispatchEvent(new CustomEvent("cookie-consent-changed", { detail: obj }));
  } catch {}
  return obj;
};

/** سياق للوصول للموافقة من أي مكان */
const CookieContext = createContext({
  consent: defaultConsent,
  setConsent: () => {},
  openManager: () => {},
});

export const useCookieConsent = () => useContext(CookieContext);

/** مزوّد + البانر */
export default function CookieBannerProvider({ children, privacyHref = "/datenschutz", imprintHref = "/impressum", logo = Logo }) {
  const [consent, setConsent] = useState(loadConsent() || defaultConsent);
  const [showBanner, setShowBanner] = useState(() => !loadConsent());
  const [openSettings, setOpenSettings] = useState(false);
  const [activeTab, setActiveTab] = useState("groups"); // groups | services | provider | history
  const [draft, setDraft] = useState(consent.categories);
  const [detailsOpen, setDetailsOpen] = useState({
    statistics: false,
    marketing: false,
    external: false,
  });

  // افتح المدير برمجياً عند الحاجة (مثلاً رابط "إعدادات الكوكيز" في الفوتر)
  const openManager = () => {
    setDraft(consent.categories);
    setActiveTab("groups");
    setOpenSettings(true);
    setShowBanner(true);
  };

  useEffect(() => {
    const handler = (e) => {
      if (e.detail === "open-cookie-manager") openManager();
    };
    window.addEventListener("cookie-manager", handler);
    return () => window.removeEventListener("cookie-manager", handler);
  }, []);

  const ctxValue = useMemo(() => ({ consent, setConsent, openManager }), [consent]);

  const acceptAll = () => {
    const newConsent = saveConsent({
      ...defaultConsent,
      categories: { essential: true, statistics: true, marketing: true, external: true },
      history: [
        ...consent.history,
        { timestamp: Date.now(), categories: { essential: true, statistics: true, marketing: true, external: true } },
      ],
    });
    setConsent(newConsent);
    setShowBanner(false);
    setOpenSettings(false);
  };

  const acceptEssentialOnly = () => {
    const cats = { essential: true, statistics: false, marketing: false, external: false };
    const newConsent = saveConsent({
      ...defaultConsent,
      categories: cats,
      history: [...consent.history, { timestamp: Date.now(), categories: cats }],
    });
    setConsent(newConsent);
    setShowBanner(false);
    setOpenSettings(false);
  };

  const saveDraft = () => {
    const cats = { ...draft, essential: true };
    const newConsent = saveConsent({
      ...defaultConsent,
      categories: cats,
      history: [...consent.history, { timestamp: Date.now(), categories: cats }],
    });
    setConsent(newConsent);
    setShowBanner(false);
    setOpenSettings(false);
  };

  // زر تبديل فئة
  const toggle = (key) => setDraft((d) => ({ ...d, [key]: !d[key] }));

    // 🎨 أزرار متناسقة مع ألوان YOUKA GmbH
  const btn = "w-full py-3 font-semibold rounded-md transition-all duration-200";
  const btnPrimary = "bg-[#7da893] hover:bg-[#6c977f] text-white shadow-md";       // ✅ Alle akzeptieren (زيتي)
  const btnSecondary = "bg-[#103d5b] hover:bg-[#0c3148] text-white shadow-md";     // ✅ Individuelle Einstellungen (أزرق داكن)
  const btnGhost = "bg-gray-100 hover:bg-gray-200 text-gray-800";                  // ✅ Speichern (رمادي محايد)
  const btnOutline = "bg-white border border-gray-300 hover:bg-gray-50 text-[#103d5b]"; // ✅ Nur essenzielle Cookies


// الرأس (شعار YOUKA في المنتصف + عنوان Datenschutz)
const Header = () => (
  <div className="relative flex items-center justify-center mb-2">
    {/* الشعار والكلمة */}
    <div className="flex flex-row items-center text-center">
      {logo ? (
        <img
          src={logo}
          alt="YOUKA GmbH Logo"
          className="h-12 sm:h-20 object-contain mb-1 mr-13"
        />
      ) : (
        <div className="text-3xl font-extrabold tracking-wide text-[#103d5b]">
          YOUKA
        </div>
      )}
      <div className="text-base sm:text-lg font-semibold text-gray-700 ml-10 mt-11">
        Cookies Banner 🍪
      </div> 
    </div>

    {/* زر الإغلاق في الزاوية العليا اليمنى */}
    {!openSettings && (
      <button
        aria-label="Schließen"
        className="absolute right-0 top-1 p-2 rounded-md hover:bg-gray-100"
        onClick={() => setShowBanner(false)}
      >
        <FiX className="text-gray-500 text-lg" />
      </button>
    )}
  </div>
);

  // صف الفئات (أيقونات نقطية رمادية مثل Doozer)
  const CategoryPills = () => (
    <div className="flex flex-wrap items-center gap-6 text-sm font-semibold text-gray-700">
      {[
        { key: "essential", label: "Essenziell", locked: true },
        { key: "statistics", label: "Statistiken" },
        { key: "marketing", label: "Marketing" },
        { key: "external", label: "Externe Medien" },
      ].map((c) => (
        <div key={c.key} className="flex items-center gap-2">
          <span
            className={`inline-block h-3 w-3 rounded-full ${
              (c.locked || draft[c.key]) ? "bg-[#10a5c7]" : "bg-gray-300"
            }`}
          />
          <span className={`${c.locked ? "opacity-90" : ""}`}>{c.label}</span>
          {c.locked && (
            <span className="text-xs text-gray-500 ml-1">(immer aktiv)</span>
          )}
        </div>
      ))}
    </div>
  );

  // تبويبات الإعدادات
  const Tabs = () => (
    <div className="flex flex-wrap gap-2 mt-6">
      {[
        { id: "groups", label: "Service-Gruppen" },
        { id: "services", label: "Services" },
        { id: "provider", label: "Provider" },
        { id: "history", label: "Einwilligung-Historie" },
      ].map((t) => (
        <button
          key={t.id}
          onClick={() => setActiveTab(t.id)}
          className={`px-4 py-2 rounded-md font-semibold text-sm transition-colors ${
            activeTab === t.id
              ? "bg-[#10a5c7] text-white"
              : "bg-[#7da893] hover:bg-[#6c977f] text-white shadow-md"
          }`}
        >
          {t.label}
        </button>
      ))}
    </div>
  );

  const Row = ({ title, desc, checked, onChange, disabled, detailKey }) => (
    <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-5 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            className="h-5 w-5 accent-[#10a5c7]"
            checked={checked}
            disabled={disabled}
            onChange={onChange}
          />
          <div className="font-semibold">{title}</div>
          {disabled && (
            <span className="text-xs text-gray-500 ml-1">(immer aktiv)</span>
          )}
        </div>
        {detailKey && (
          <button
            className="text-sm text-[#10a5c7] hover:underline flex items-center gap-1"
            onClick={() =>
              setDetailsOpen((p) => ({ ...p, [detailKey]: !p[detailKey] }))
            }
          >
            {detailsOpen[detailKey] ? "Cookie-Informationen ausblenden" : "Cookie-Informationen anzeigen"}
            <FiChevronDown
              className={`transition-transform ${detailsOpen[detailKey] ? "rotate-180" : ""}`}
            />
          </button>
        )}
      </div>
      <p className="text-sm text-gray-600">{desc}</p>
      {detailKey && detailsOpen[detailKey] && (
        <div className="mt-2 border-t pt-3 text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <FiCheck className="text-[#10a5c7]" />
            <span>Speicherdauer: 13 Monate</span>
          </div>
          <div className="flex items-center gap-2">
            <FiCheck className="text-[#10a5c7]" />
            <span>Zweck: {detailKey === "statistics" ? "Reichweitenmessung" : detailKey === "marketing" ? "Personalisierte Anzeigen" : "Externe Inhalte (z. B. YouTube) freischalten"}</span>
          </div>
        </div>
      )}
    </div>
  );

  const SettingsView = () => (
    <>
      <button
        className="mt-4 mb-2 flex items-center gap-2 text-sm text-[#103d5b] hover:underline"
        onClick={() => {
          setOpenSettings(false);
          setActiveTab("groups");
        }}
      >
        <FiChevronLeft /> Zurück
      </button>

      <Tabs />

      <div className="mt-6 space-y-4 max-h-[50vh] overflow-auto pr-1">
        {activeTab === "groups" && (
          <>
            <Row
              title="Essenziell"
              desc="Essenzielle Cookies ermöglichen grundlegende Funktionen und sind für die einwandfreie Funktion der Website erforderlich."
              checked
              disabled
            />
            <Row
              title="Statistiken"
              desc="Hilft uns zu verstehen, wie Besucher unsere Website nutzen (anonym)."
              checked={draft.statistics}
              onChange={() => toggle("statistics")}
              detailKey="statistics"
            />
            <Row
              title="Marketing"
              desc="Cookies für personalisierte Anzeigen und Tracking über Websites hinweg."
              checked={draft.marketing}
              onChange={() => toggle("marketing")}
              detailKey="marketing"
            />
            <Row
              title="Externe Medien"
              desc="Erlaubt das Laden externer Inhalte wie YouTube-Videos, Karten usw."
              checked={draft.external}
              onChange={() => toggle("external")}
              detailKey="external"
            />
          </>
        )}

        {activeTab === "services" && (
          <div className="space-y-4">
            <Row
              title="Google Tag Manager"
              desc="Verwaltung von Tags. Es werden nur die aktivierten Kategorien ausgelöst."
              checked={draft.statistics || draft.marketing}
              onChange={() =>
                setDraft((d) => ({ ...d, statistics: !d.statistics, marketing: !d.marketing }))
              }
            />
            <Row
              title="YouTube Embeds"
              desc="Externe Medien – spielt Videos erst nach Zustimmung ab."
              checked={draft.external}
              onChange={() => toggle("external")}
            />
          </div>
        )}

        {activeTab === "provider" && (
          <div className="space-y-4 text-sm text-gray-700">
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="font-semibold">Google Ireland Ltd.</div>
              <div>Standort: Dublin, IE</div>
              <div>Zweck: Analytics / Tag Management / Ads</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="font-semibold">YouTube / Google LLC</div>
              <div>Standort: USA</div>
              <div>Zweck: Externe Medien (Video)</div>
            </div>
          </div>
        )}

        {activeTab === "history" && (
          <div className="space-y-3 text-sm">
            {consent.history?.length ? (
              consent.history
                .slice()
                .reverse()
                .map((h, i) => (
                  <div key={i} className="bg-white border border-gray-200 rounded-lg p-3">
                    <div className="font-semibold">
                      {new Date(h.timestamp).toLocaleString()}
                    </div>
                    <div className="mt-1 text-gray-700">
                      {Object.entries(h.categories)
                        .map(([k, v]) => `${k}: ${v ? "an" : "aus"}`)
                        .join(" • ")}
                    </div>
                  </div>
                ))
            ) : (
              <div className="text-gray-600">Keine Einträge vorhanden.</div>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-6">
        <button className={`${btn} ${btnGhost}`} onClick={saveDraft}>Speichern</button>
        <button className={`${btn} ${btnPrimary}`} onClick={acceptAll}>Alle akzeptieren</button>
        <button className={`${btn} ${btnOutline}`} onClick={acceptEssentialOnly}>
          Nur essenzielle Cookies akzeptieren
        </button>
      </div>
    </>
  );

  if (!showBanner) {
    return (
      <CookieContext.Provider value={ctxValue}>
        {children}
      </CookieContext.Provider>
    );
  }

  return (
    <CookieContext.Provider value={ctxValue}>
      {children}

      {/* خلفية تغطية */}
      <div className="fixed inset-0 z-[9998] bg-black/40" />

      {/* المودال */}
      <div className="fixed inset-0 z-[9999] flex items-start sm:items-center justify-center p-4">
        <div className="w-full max-w-3xl bg-white rounded-2xl shadow-2xl border border-gray-200">
          <div className="p-5 sm:p-7">
            <Header />

            {/* خط فاصل ملون مثل Doozer */}
            <div className="h-[3px] bg-[#10a5c7] my-5" />

            {!openSettings ? (
              <>
                <p className="text-sm text-gray-700 leading-relaxed">
                  Wir nutzen Cookies und andere Technologien auf unserer Website. Einige sind essenziell,
                  während andere uns helfen, diese Website und Ihre Erfahrung zu verbessern. Personenbezogene
                  Daten können verarbeitet werden (z. B. IP-Adressen), z. B. für personalisierte Anzeigen und
                  Inhalte oder Anzeigen- und Inhaltsmessung. Sie können Ihre Auswahl jederzeit unter
                  <button
                    onClick={() => { setOpenSettings(true); setActiveTab("groups"); }}
                    className="ml-1 text-[#10a5c7] hover:underline"
                  >
                    Einstellungen
                  </button>{" "}
                  widerrufen oder anpassen.
                </p>

                <div className="mt-6">
                  <CategoryPills />
                </div>

                <div className="grid grid-cols-1 gap-3 mt-6">
                  <button className={`${btn} ${btnPrimary}`} onClick={acceptAll}>
                    Alle akzeptieren
                  </button>
                  <button
                    className={`${btn} ${btnSecondary}`}
                    onClick={() => setOpenSettings(true)}
                  >
                    Individuelle Datenschutzeinstellungen
                  </button>
                  <button className={`${btn} ${btnGhost}`} onClick={saveDraft}>
                    Speichern
                  </button>
                  <button className={`${btn} ${btnOutline}`} onClick={acceptEssentialOnly}>
                    Nur essenzielle Cookies akzeptieren
                  </button>
                </div>

                <div className="flex flex-wrap items-center justify-center gap-4 mt-6 text-sm">
                  <div className="flex flex-wrap gap-6">
                    <a
                      href="#cookie-details"
                      className="text-gray-500 hover:text-[#103d5b]"
                    >
                      Cookie-Details
                    </a>
                    <a
                      href={privacyHref}
                      className="text-gray-500 hover:text-[#103d5b]"
                    >
                      Datenschutzerklärung
                    </a>
                    <a
                      href={imprintHref}
                      className="text-gray-500 hover:text-[#103d5b]"
                    >
                      Impressum
                    </a>
                  </div>
                </div>
              </>
            ) : (
              <SettingsView />
            )}
          </div>
        </div>
      </div>
    </CookieContext.Provider>
  );
}

/* ===================== طريقة الاستخدام =====================

1) أضف المكوّن حول تطبيقك (عادة في App.jsx):

import CookieBannerProvider from "./components/CookieBanner";
import logo from "./assets/img/Logo.png"; // اختياري

function App() {
  return (
    <CookieBannerProvider logo={logo} privacyHref="/datenschutz" imprintHref="/impressum">
      <YourRoutesAndLayout />
    </CookieBannerProvider>
  );
}

2) لاستخدام الموافقات في أي مكان (مثلاً لتفعيل Google Analytics فقط عند الموافقة):

import { useCookieConsent } from "../components/CookieBanner";

const MyComponent = () => {
  const { consent } = useCookieConsent();
  useEffect(() => {
    if (consent.categories.statistics) {
      // initAnalytics();
    }
  }, [consent]);
  return null;
};

3) لفتح مدير الكوكيز من رابط الفوتر:
  window.dispatchEvent(new CustomEvent("cookie-manager", { detail: "open-cookie-manager" }));

============================================================= */
